/**
 * Partner Users Page
 *
 * View and manage users across partner organizations
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  Filter,
  UserPlus,
  Building2,
  CheckCircle,
  XCircle,
  ArrowLeft
} from 'lucide-react';
import { apiService } from '@/services/api';
import { toast } from 'react-hot-toast';
import { DataTable, ColumnDefinition, SortState } from '@/components/shared/DataTable';
import { getRoleDisplayName, getRoleBadgeClasses } from '@/utils/roleMapping';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  userScope: string;
  organizationId?: string;
  organizationName?: string;
  partnerId?: string;
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
}

interface Organization {
  id: string;
  name: string;
}

const PartnerUsers: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<{
    organizationUsers: User[];
    partnerUsers: User[];
    totalCount: number;
  }>({ organizationUsers: [], partnerUsers: [], totalCount: 0 });
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [sortState, setSortState] = useState<SortState>({ column: 'firstName', direction: 'asc' });

  useEffect(() => {
    loadData();
  }, [roleFilter, orgFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (orgFilter !== 'all') params.append('organizationId', orgFilter);

      // Define response types matching backend API structure
      interface UsersResponse { success: boolean; data: { partnerUsers: User[]; organizationUsers: User[] } }
      interface OrgsResponse { success: boolean; data: { organizations: Organization[] } }

      const [usersRes, orgsRes] = await Promise.all([
        apiService.get<UsersResponse>(`/v1/partner/users?${params.toString()}`),
        apiService.get<OrgsResponse>('/v1/partner/organizations')
      ]);

      setUsers({
        partnerUsers: usersRes.data?.partnerUsers || [],
        organizationUsers: usersRes.data?.organizationUsers || [],
        totalCount: (usersRes.data?.partnerUsers?.length || 0) + (usersRes.data?.organizationUsers?.length || 0)
      });
      setOrganizations(orgsRes.data?.organizations || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const allUsers = [...users.partnerUsers, ...users.organizationUsers];
  const filteredUsers = allUsers.filter(user =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sorted data
  const sortedUsers = useMemo(() => {
    if (!sortState.column || !sortState.direction) return filteredUsers;

    const getValue = (obj: any, path: string) =>
      path.split('.').reduce((acc, part) => acc?.[part], obj);

    return [...filteredUsers].sort((a, b) => {
      const aVal = getValue(a, sortState.column!);
      const bVal = getValue(b, sortState.column!);
      if (aVal == null) return sortState.direction === 'asc' ? 1 : -1;
      if (bVal == null) return sortState.direction === 'asc' ? -1 : 1;
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal)
        : Number(aVal) - Number(bVal);
      return sortState.direction === 'asc' ? cmp : -cmp;
    });
  }, [filteredUsers, sortState]);

  // Colours and labels both come from the shared role map. The local switch knew only the earlier
  // partner spellings, so channel_partner_admin fell to the default grey.

  // DataTable column definitions
  const userColumns: ColumnDefinition<User>[] = useMemo(() => [
    {
      id: 'user',
      header: 'User',
      sortable: true,
      sortKey: 'firstName',
      accessor: (user) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {user.firstName} {user.lastName}
          </div>
          <div className="text-xs text-gray-500">{user.email}</div>
        </div>
      ),
    },
    {
      id: 'role',
      header: 'Role',
      sortable: true,
      sortKey: 'role',
      accessor: (user) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeClasses(user.role)}`}>
          {getRoleDisplayName(user.role)}
        </span>
      ),
    },
    {
      id: 'organization',
      header: 'Organization',
      sortable: true,
      sortKey: 'organizationName',
      accessor: (user) => (
        <span className="text-sm text-gray-700">
          {user.organizationName || 'Partner Level'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      sortKey: 'isActive',
      accessor: (user) => user.isActive ? (
        <div className="flex items-center gap-1 text-green-600">
          <CheckCircle className="h-4 w-4" />
          <span className="text-sm">Active</span>
        </div>
      ) : (
        <div className="flex items-center gap-1 text-red-600">
          <XCircle className="h-4 w-4" />
          <span className="text-sm">Inactive</span>
        </div>
      ),
    },
    {
      id: 'lastLogin',
      header: 'Last Login',
      sortable: true,
      sortKey: 'lastLogin',
      accessor: (user) => (
        <span className="text-sm text-gray-500">
          {user.lastLogin
            ? new Date(user.lastLogin).toLocaleString()
            : 'Never'}
        </span>
      ),
    },
  ], []);

  // No row actions: edit pointed at /partner/users/:id/edit, which has no route, and both
  // edit and delete are requirePartnerAdmin endpoints that no role reaching this portal holds.

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/partner')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
              <p className="text-sm text-gray-500 mt-1">
                Manage users across your organizations
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-semibold text-gray-900 mt-1">
                  {users.totalCount}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Partner Users</p>
                <p className="text-2xl font-semibold text-gray-900 mt-1">
                  {users.partnerUsers.length}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-violet-100 flex items-center justify-center">
                <UserPlus className="h-6 w-6 text-violet-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Organization Users</p>
                <p className="text-2xl font-semibold text-gray-900 mt-1">
                  {users.organizationUsers.length}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent w-64"
                  />
                </div>

                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
                >
                  <option value="all">All Roles</option>
                  {/* This endpoint returns organization-scoped users only, so those are the roles it
                      can filter. Labels come from the shared map so they match the badges. */}
                  {['admin', 'operator', 'staff'].map(role => (
                    <option key={role} value={role}>{getRoleDisplayName(role)}</option>
                  ))}
                </select>

                <select
                  value={orgFilter}
                  onChange={(e) => setOrgFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
                >
                  <option value="all">All Organizations</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <DataTable
            data={sortedUsers}
            columns={userColumns}
            striped={true}
            hoverable={true}
            sortable={true}
            sortState={sortState}
            onSortChange={setSortState}
            getRowId={(user) => user.id}
            ariaLabel="Partner users table"
            emptyMessage="No users found"
            emptyIcon={<Users className="h-12 w-12 text-gray-300" />}
          />
        </div>
      </main>
    </div>
  );
};

export default PartnerUsers;
